import { ReactNode, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { ITask } from '../reducers'

type Action = 'DONE' | 'REJECTED'

const TITRE: Record<Action, string> = {
  DONE: 'Marquer la tâche comme traitée',
  REJECTED: 'Rejeter la tâche',
}

const INTITULE: Record<Action, string> = {
  DONE: 'Traiter',
  REJECTED: 'Rejeter',
}

type Avertissement = {
  gravite: 'warning' | 'info' | 'success'
  texte: ReactNode
}

/**
 * Ce que le dialogue annonce avant le clic. Quatre cas exclusifs, dans l'ordre.
 *
 * 1. **Le rejet définitif.** Le seul geste irréversible de cet écran : il
 *    déclare la référence hors périmètre de facturation, pour tous les centres,
 *    et data-nest cesse de la signaler. Il passe en premier parce que le
 *    message suivant dirait exactement le contraire.
 * 2. **La cause déjà levée par un rejet.** La tâche jumelle d'un autre centre a
 *    été rejetée. Dire « le problème n'existe plus » serait faux : il est
 *    intact, c'est le signalement qui a cessé.
 * 3. **La cause levée par une correction.** Le message d'origine.
 * 4. **La cause qui tient.** `addTask` ne déduplique que sur les tâches
 *    ouvertes : fermer sans corriger la donnée en fait réapparaître une
 *    identique au cycle suivant. C'est voulu, mais sans le dire ici, les
 *    utilisateurs y voient un bug.
 *
 * Le premier cas dépend de `action`, qui change entre « Traiter » et
 * « Rejeter » sans fermer le dialogue : le message se recalcule à chaque clic.
 */
function avertir(task: ITask, action: Action | null): Avertissement {
  if (action === 'REJECTED' && task.rejectionEndsSignal) {
    return {
      gravite: 'warning',
      texte: (
        <>
          Rejeter, c’est déclarer cette référence hors périmètre de
          facturation : elle ne sera plus signalée,{' '}
          <strong>dans aucun centre</strong>. Les patients qui la portent
          resteront non facturés, et poser un tarif plus tard ne rouvrira pas la
          tâche. Pour dire seulement que vous avez traité le sujet, utilisez
          « Traiter ».
        </>
      ),
    }
  }
  if (task.causeLiftedReason === 'OUT_OF_SCOPE') {
    return {
      gravite: 'success',
      texte:
        'Cette référence a déjà été déclarée hors périmètre de facturation : cette tâche ne réapparaîtra pas.',
    }
  }
  if (task.causeStillHolds === false) {
    return {
      gravite: 'success',
      texte:
        'Le problème signalé n’existe plus dans les données : cette tâche ne réapparaîtra pas.',
    }
  }
  return {
    gravite: 'info',
    texte:
      'Si le problème n’est pas corrigé dans les données, cette tâche sera recréée automatiquement au prochain cycle de transmission.',
  }
}

export const TaskActions = ({
  task,
  onTransition,
  onComment,
}: {
  task: ITask
  onTransition: (
    id: string,
    status: Action,
    comment: string | undefined,
  ) => Promise<unknown>
  onComment: (id: string, comment: string) => Promise<unknown>
}) => {
  const [action, setAction] = useState<Action | null>(null)
  const [commentaire, setCommentaire] = useState('')
  const [enCours, setEnCours] = useState(false)
  const avertissement = avertir(task, action)

  const fermer = () => {
    setAction(null)
    setCommentaire('')
  }

  const valider = () => {
    if (!action) {
      return
    }
    setEnCours(true)
    onTransition(task.id, action, commentaire.trim() || undefined)
      .then(() => {
        toast.success(
          action === 'DONE' ? 'Tâche traitée.' : 'Tâche rejetée.',
        )
        fermer()
      })
      .catch((raison: unknown) =>
        toast.error(
          typeof raison === 'string' ? raison : 'Le traitement a échoué.',
        ),
      )
      .finally(() => setEnCours(false))
  }

  const commenterSeulement = () => {
    const texte = commentaire.trim()
    if (!texte) {
      return
    }
    setEnCours(true)
    onComment(task.id, texte)
      .then(() => {
        toast.success('Commentaire ajouté.')
        setCommentaire('')
      })
      .catch(() => toast.error("L'ajout du commentaire a échoué."))
      .finally(() => setEnCours(false))
  }

  // Une tâche fermée reste commentable — pour mémoire, sans la rouvrir.
  if (!task.isOpen) {
    return (
      <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          label="Ajouter un commentaire"
          value={commentaire}
          onChange={(e) => setCommentaire(e.target.value)}
        />
        <Button
          onClick={commenterSeulement}
          disabled={enCours || !commentaire.trim()}
        >
          Commenter
        </Button>
      </Stack>
    )
  }

  return (
    <>
      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setAction('DONE')}
        >
          Traiter
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => setAction('REJECTED')}
        >
          Rejeter
        </Button>
      </Stack>

      <Dialog open={action !== null} onClose={fermer} fullWidth maxWidth="sm">
        <DialogTitle>{action ? TITRE[action] : ''}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>{task.name}</DialogContentText>

          <Alert severity={avertissement.gravite} sx={{ mb: 2 }}>
            {avertissement.texte}
          </Alert>

          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Commentaire (facultatif)"
            value={commentaire}
            onChange={(e) => setCommentaire(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fermer} disabled={enCours}>
            Annuler
          </Button>
          <Button variant="contained" onClick={valider} disabled={enCours}>
            {action ? INTITULE[action] : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TaskActions
